import React, {
    useEffect, useId, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import apiService from '../../../services/apiService';
import {
    Button, Dialog, ErrorState, IconButton, LoadingState, SegmentedControl,
} from '../../ui';
import UserSearch from '../../shared/UserSearch';
import CancelTaskConfirm from '../../shared/CancelTaskConfirm';
import { formatCurrency } from '../../../utils/formatting';
import {
    formatDueDate, toDateInputValue, toDueDatePayload, undoToastText,
} from '../../../utils/taskBoard';
import useQuickGuard from '../../../hooks/useQuickGuard';
import {
    ACTIVITY_FILTERS, activityStatus, activityType, buildCreatePayload, emptyActivitiesText, filterActivities,
    filterLabel, fmtActivityDate, isActivityOverdue, isClosedActivity, isOpenActivity, splitDetails,
    statusChangeFields,
} from '../../../utils/unitActivities';
import { tabCtxShape } from './tabContext';
import AiDescription from './AiDescription';
import './ActivityTabV2.css';


const EditIcon = () => (
    <svg viewBox="0 0 20 20" fill="currentColor" width="13" height="13" aria-hidden="true" focusable="false">
        <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
    </svg>
);
const TrashIcon = () => (
    <svg viewBox="0 0 20 20" fill="currentColor" width="13" height="13" aria-hidden="true" focusable="false">
        <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
    </svg>
);

// Escape inside an inline layer (edit form, confirm) closes that layer only:
// it must not reach the page, which goes back on Escape (D6).
const escapeOnly = (fn) => (e) => {
    if (e.key !== 'Escape') return;
    e.stopPropagation();
    fn();
};

// ── Create task dialog ───────────────────────────────────────────────────

function CreateTaskDialog({
    unit, users, activities, draft, onDraft, onDiscard, onClose, onCreated,
}) {
    const id = useId().replace(/:/g, '');
    const [type, setType] = useState(draft?.type ?? 'price_change');
    const [title, setTitle] = useState(draft?.title ?? '');
    const [details, setDetails] = useState(draft?.details ?? '');
    const [suggestedPrice, setSuggestedPrice] = useState(draft?.suggestedPrice ?? '');
    const [assignedTo, setAssignedTo] = useState(draft?.assignedTo ?? '');
    const [dueDate, setDueDate] = useState(draft?.dueDate ?? '');
    const [submitting, setSubmitting] = useState(false);
    const busy = useRef(false);
    const titleRef = useRef(null);

    // What was typed survives closing with Escape or the scrim; only Discard
    // and a successful create drop it.
    useEffect(() => {
        onDraft({ type, title, details, suggestedPrice, assignedTo, dueDate });
    }, [type, title, details, suggestedPrice, assignedTo, dueDate]); // eslint-disable-line react-hooks/exhaustive-deps

    const submit = async (e) => {
        e.preventDefault();
        if (!title.trim() || busy.current) return;
        busy.current = true;
        setSubmitting(true);
        try {
            await apiService.createActivity(buildCreatePayload(unit, {
                type, title, details, suggestedPrice, assignedTo, dueDate,
            }));
            onDiscard();
            onCreated(assignedTo || null);
            onClose();
        } catch (err) {
            console.error('Failed to create activity', err); // eslint-disable-line no-console
            busy.current = false;
            setSubmitting(false);
        }
    };

    const formId = `${id}-form`;
    return (
        <Dialog
            open
            onClose={onClose}
            title={`Create task · #${unit.StockNumber}`}
            initialFocusRef={titleRef}
            footer={(
                <>
                    {/* "Discard", not "Cancel": "Cancel" is the task status. */}
                    <Button onClick={() => { onDiscard(); onClose(); }}>Discard</Button>
                    <Button variant="primary" type="submit" form={formId} disabled={submitting || !title.trim()}>
                        {submitting ? 'Creating...' : 'Create task'}
                    </Button>
                </>
            )}
        >
            <form id={formId} className="u2-act-form" onSubmit={submit}>
                <label className="u2-act-fl" htmlFor={`${id}-type`}>
                    <span>Type</span>
                    <select id={`${id}-type`} className="u2-act-in" value={type} onChange={(e) => setType(e.target.value)}>
                        <option value="price_change">Price Change</option>
                        <option value="photo_update">Photo Update</option>
                        <option value="description">Description Improvement</option>
                        <option value="other">Other</option>
                    </select>
                </label>
                <label className="u2-act-fl" htmlFor={`${id}-title`}>
                    <span>Title</span>
                    <input
                        id={`${id}-title`}
                        ref={titleRef}
                        className="u2-act-in"
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="e.g. Reduce price to $350,000"
                        required
                        maxLength={800}
                    />
                </label>
                {type === 'price_change' && (
                    <div className="u2-act-two">
                        <label className="u2-act-fl" htmlFor={`${id}-cur`}>
                            <span>Current price</span>
                            <input id={`${id}-cur`} className="u2-act-in" type="text" value={formatCurrency(unit.Best_Sell_Price)} disabled />
                        </label>
                        <label className="u2-act-fl" htmlFor={`${id}-sug`}>
                            <span>Suggested price</span>
                            <input
                                id={`${id}-sug`}
                                className="u2-act-in"
                                type="number"
                                value={suggestedPrice}
                                onChange={(e) => setSuggestedPrice(e.target.value)}
                                placeholder="350000"
                                min={0}
                                step={1000}
                            />
                        </label>
                    </div>
                )}
                <div className="u2-act-two">
                    <div className="u2-act-fl">
                        <span>Assign to</span>
                        <UserSearch
                            users={users}
                            value={assignedTo}
                            onChange={setAssignedTo}
                            activities={activities}
                            placeholder="Search users..."
                        />
                    </div>
                    <label className="u2-act-fl" htmlFor={`${id}-due`}>
                        <span>Due date</span>
                        <input id={`${id}-due`} className="u2-act-in" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
                    </label>
                </div>
                <label className="u2-act-fl" htmlFor={`${id}-notes`}>
                    <span>Notes</span>
                    <textarea
                        id={`${id}-notes`}
                        className="u2-act-in"
                        value={details}
                        onChange={(e) => setDetails(e.target.value)}
                        placeholder="Additional context..."
                        rows={3}
                    />
                </label>
            </form>
        </Dialog>
    );
}

CreateTaskDialog.propTypes = {
    unit: PropTypes.object.isRequired,
    users: PropTypes.array.isRequired,
    activities: PropTypes.array.isRequired,
    draft: PropTypes.object,
    onDraft: PropTypes.func.isRequired,
    onDiscard: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    onCreated: PropTypes.func.isRequired,
};

// ── One task ─────────────────────────────────────────────────────────────


function TaskCard({
    activity, canEdit, users, activities, onStatusChange, onQuickStatus, onAssign, onEdit, onDelete,
}) {
    const id = useId().replace(/:/g, '');
    const [editing, setEditing] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [confirmCancel, setConfirmCancel] = useState(false);
    const [editTitle, setEditTitle] = useState(activity.title);
    const [editDetails, setEditDetails] = useState(activity.details || '');
    const [editDue, setEditDue] = useState(toDateInputValue(activity.due_date));
    // Where focus goes when an inline layer closes, so keyboard users keep their place.
    const cancelBtn = useRef(null);
    const editBtn = useRef(null);
    const deleteBtn = useRef(null);
    const noBtn = useRef(null);
    const refocus = useRef(null);
    useEffect(() => {
        const target = refocus.current;
        if (!target) return;
        refocus.current = null;
        target.current?.focus();
    });
    useEffect(() => { if (confirmDelete) noBtn.current?.focus(); }, [confirmDelete]);

    const type = activityType(activity.type);
    const status = activityStatus(activity.status);
    const open = isOpenActivity(activity);
    const closed = isClosedActivity(activity);
    const actionable = canEdit && open;
    const overdue = isActivityOverdue(activity);
    const { rationale, aiDescription } = splitDetails(activity.details);

    const startEdit = () => {
        setEditTitle(activity.title);
        setEditDetails(activity.details || '');
        setEditDue(toDateInputValue(activity.due_date));
        setConfirmCancel(false);
        setConfirmDelete(false);
        setEditing(true);
    };
    const discardEdit = () => { setEditing(false); refocus.current = editBtn; };
    const saveEdit = () => {
        onEdit(activity.id, { title: editTitle, details: editDetails, due_date: toDueDatePayload(editDue) });
        setEditing(false);
        refocus.current = editBtn;
    };
    const keepTask = () => { setConfirmCancel(false); refocus.current = cancelBtn; };
    const keepDelete = () => { setConfirmDelete(false); refocus.current = deleteBtn; };

    if (editing) {
        return (
            <div className="u2-act-task" data-task={activity.id}>
                {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                <div className="u2-act-form" role="group" aria-label="Edit task" onKeyDown={escapeOnly(discardEdit)}>
                    <label className="u2-act-fl" htmlFor={`${id}-t`}>
                        <span>Title</span>
                        <input id={`${id}-t`} className="u2-act-in" type="text" value={editTitle} onChange={(e) => setEditTitle(e.target.value)} maxLength={800} />
                    </label>
                    <label className="u2-act-fl" htmlFor={`${id}-d`}>
                        <span>Due date</span>
                        <input id={`${id}-d`} className="u2-act-in" type="date" value={editDue} onChange={(e) => setEditDue(e.target.value)} />
                    </label>
                    <label className="u2-act-fl" htmlFor={`${id}-n`}>
                        <span>Details</span>
                        <textarea id={`${id}-n`} className="u2-act-in" value={editDetails} onChange={(e) => setEditDetails(e.target.value)} rows={3} />
                    </label>
                    <div className="u2-act-row">
                        <Button variant="primary" size="sm" onClick={saveEdit} disabled={!editTitle.trim()}>Save changes</Button>
                        <Button size="sm" onClick={discardEdit}>Discard changes</Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className={`u2-act-task${closed ? ' is-closed' : ''}${overdue ? ' is-overdue' : ''}`}
            data-task={activity.id}
        >
            <div className="u2-act-th">
                <span className={`u2-act-st u2-act-st--${status.tone}`}>{status.label}</span>
                <b className="u2-act-title">{activity.title}</b>
                <span className={`u2-act-type u2-act-type--${type.tone}`}>{type.label}</span>
            </div>
            <div className="u2-act-meta">
                <span>{activity.created_by}</span>
                <span aria-hidden="true">→</span>
                {actionable ? (
                    <span className="u2-act-assign">
                        <UserSearch
                            users={users}
                            value={activity.assigned_to || ''}
                            onChange={(val) => onAssign(activity.id, val || null)}
                            activities={activities}
                            placeholder="Assign..."
                            variant="inline"
                        />
                    </span>
                ) : (
                    <b>{activity.assigned_to || 'Unassigned'}</b>
                )}
                <span>{fmtActivityDate(activity.created_at)}</span>
                {activity.due_date && (
                    <span className={overdue ? 'u2-act-late' : undefined}>Due {formatDueDate(activity.due_date)}</span>
                )}
            </div>
            {rationale && <p className="u2-note u2-act-notes">{rationale}</p>}
            {aiDescription !== null && aiDescription !== '' && <AiDescription text={aiDescription} />}
            {activity.type === 'price_change' && activity.suggested_price != null && (
                <p className="u2-act-price">
                    {activity.current_price != null && <span>{formatCurrency(activity.current_price)}</span>}
                    <span aria-hidden="true"> → </span>
                    <b>{formatCurrency(activity.suggested_price)}</b>
                </p>
            )}
            {activity.completed_at && (
                <p className="u2-act-done">Completed {fmtActivityDate(activity.completed_at)} by {activity.completed_by}</p>
            )}
            {canEdit && (
                <div className="u2-act-row">
                    {actionable && (confirmCancel ? (
                        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                        <span className="u2-act-confirm" onKeyDown={escapeOnly(keepTask)}>
                            <CancelTaskConfirm
                                classPrefix="u2-act"
                                buttonClass="v2-btn v2-btn--default v2-btn--sm"
                                dangerClass="u2-act-danger"
                                onKeep={keepTask}
                                onConfirm={() => { setConfirmCancel(false); onStatusChange(activity, 'cancelled'); }}
                            />
                        </span>
                    ) : (
                        <>
                            {activity.status === 'open' && (
                                <Button size="sm" onClick={(e) => onQuickStatus(activity, 'in_progress', e)}>Start</Button>
                            )}
                            <Button size="sm" onClick={(e) => onQuickStatus(activity, 'completed', e)}>Complete</Button>
                            <Button size="sm" className="u2-act-danger" ref={cancelBtn} onClick={() => setConfirmCancel(true)}>
                                Cancel task
                            </Button>
                        </>
                    ))}
                    {closed && (
                        <Button size="sm" onClick={(e) => onQuickStatus(activity, 'open', e)}>Reopen task</Button>
                    )}
                    <span className="u2-act-spacer" />
                    {confirmDelete ? (
                        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                        <span className="u2-act-confirm" role="group" aria-label="Confirm delete" onKeyDown={escapeOnly(keepDelete)}>
                            Delete?
                            <Button size="sm" className="u2-act-danger" onClick={() => onDelete(activity.id)}>Yes</Button>
                            <Button size="sm" ref={noBtn} onClick={keepDelete}>No</Button>
                        </span>
                    ) : (
                        <>
                            <IconButton label="Edit task" ref={editBtn} onClick={startEdit}><EditIcon /></IconButton>
                            <IconButton label="Delete task" ref={deleteBtn} onClick={() => setConfirmDelete(true)}><TrashIcon /></IconButton>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

TaskCard.propTypes = {
    activity: PropTypes.object.isRequired,
    canEdit: PropTypes.bool.isRequired,
    users: PropTypes.array.isRequired,
    activities: PropTypes.array.isRequired,
    onStatusChange: PropTypes.func.isRequired,
    onQuickStatus: PropTypes.func.isRequired,
    onAssign: PropTypes.func.isRequired,
    onEdit: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
};

// ── The tab ──────────────────────────────────────────────────────────────

/**
 * Activity on the full unit page (P-UNIT-116..126, D11). The list is the
 * page's useUnitActivities instance (one request for the hero chip, the tab
 * count and this tab). Today's rules come from utils/unitActivities.js.
 * New on the page: toasts for every change, Undo on "Task completed" and
 * "Task cancelled" (D11), Escape closes the innermost layer only (D6).
 */
export default function ActivityTabV2({ ctx }) {
    const {
        unit, activities: list, readOnly, showToast, createNonce = 0, onCreateConsumed,
    } = ctx;
    const canEdit = !readOnly;
    const {
        activities, loading, error: listError, fetchActivities, applyUpdate, removeLocal,
    } = list;
    const [filter, setFilter] = useState('all');
    const [users, setUsers] = useState([]);
    const [showCreate, setShowCreate] = useState(createNonce > 0 && canEdit);
    const draft = useRef(null);
    const newTaskBtn = useRef(null);

    useEffect(() => {
        apiService.getActivityUsers().then((d) => setUsers(d.users || [])).catch(() => {});
    }, []);

    // The hero's "Create task" (and arriving from the inventory's) opens the
    // form, then the request is consumed (onCreateConsumed sets createNonce
    // back to 0), so revisiting the tab does not reopen it (R2-B4).
    const seenNonce = useRef(0);
    useEffect(() => {
        if (createNonce === seenNonce.current) return;
        seenNonce.current = createNonce;
        if (createNonce > 0 && canEdit) {
            setShowCreate(true);
            onCreateConsumed?.();
        }
    }, [createNonce, canEdit]); // eslint-disable-line react-hooks/exhaustive-deps

    const fail = (what, err) => {
        console.error(`Failed to ${what}`, err); // eslint-disable-line no-console
        showToast({ text: `Could not ${what}. Please try again.` });
    };

    const putStatus = async (activityId, status) => {
        const saved = await apiService.updateActivityStatus(activityId, { status });
        applyUpdate(activityId, saved, statusChangeFields(status));
    };

    const handleStatusChange = async (activity, status) => {
        const previous = activity.status;
        try {
            await putStatus(activity.id, status);
        } catch (err) {
            fail('update the task', err);
            return;
        }
        // D11 / QA T-3: the board's rule — a toast (with Undo) only after
        // completing or cancelling; Start and Reopen stay silent.
        const text = undoToastText(previous, status);
        if (!text) return;
        showToast({
            text,
            onUndo: () => { putStatus(activity.id, previous).catch((err) => fail('undo the change', err)); },
        });
    };

    // D11: the board's 400 ms quick guard on Start / Complete / Reopen.
    const quickGuard = useQuickGuard();
    const handleQuickStatus = (activity, status, e) => {
        if (!quickGuard(e)) return;
        handleStatusChange(activity, status);
    };

    const handleAssign = async (activityId, assignee) => {
        try {
            const updates = { assigned_to: assignee };
            const saved = await apiService.updateActivity(activityId, updates);
            applyUpdate(activityId, saved, updates);
            showToast({ text: assignee ? `Task assigned to ${assignee}` : 'Task unassigned' });
        } catch (err) {
            fail('reassign the task', err);
        }
    };

    const handleEdit = async (activityId, updates) => {
        try {
            const saved = await apiService.updateActivity(activityId, updates);
            applyUpdate(activityId, saved, updates);
            showToast({ text: 'Changes saved' });
        } catch (err) {
            fail('save the changes', err);
        }
    };

    // Deleting is confirmed inline (Delete? Yes / No) and happens at once, as
    // today. No Undo: holding the DELETE back until a toast expires could be
    // lost on a reload, and re-creating would change the task's id and author.
    const handleDelete = async (activityId) => {
        try {
            await apiService.deleteActivity(activityId);
            removeLocal(activityId);
            showToast({ text: 'Task deleted' });
        } catch (err) {
            fail('delete the task', err);
        }
    };

    const openCreate = () => setShowCreate(true);
    const closeCreate = () => {
        setShowCreate(false);
        newTaskBtn.current?.focus();
    };

    if (loading) return <LoadingState label="Loading activities..." />;
    // A failed read is an error with Retry, never "No activities yet" (QA P-2).
    if (listError) return <ErrorState title="Tasks could not be loaded" message={listError} onRetry={() => fetchActivities()} />;

    const open = activities.filter(isOpenActivity).length;
    const closed = activities.length - open;
    const shown = filterActivities(activities, filter);

    return (
        <div className="u2-card u2-act">
            <div className="u2-c-head">
                <h3>Tasks for this unit</h3>
                <span className="u2-sub">{open} open · {closed} closed</span>
                {canEdit && (
                    <span className="u2-right">
                        <Button variant="primary" size="sm" ref={newTaskBtn} onClick={openCreate}>+ New task</Button>
                    </span>
                )}
            </div>
            <SegmentedControl
                label="Filter tasks by status"
                className="u2-act-filters"
                value={filter}
                onChange={setFilter}
                options={ACTIVITY_FILTERS.map((f) => ({ value: f, label: filterLabel(f) }))}
            />
            {shown.length === 0 ? (
                <div className="u2-act-empty">
                    <p>{emptyActivitiesText(filter)}</p>
                    {canEdit && filter === 'all' && (
                        <Button onClick={openCreate}>Create the first request</Button>
                    )}
                </div>
            ) : (
                <div className="u2-act-list">
                    {shown.map((a) => (
                        <TaskCard
                            key={a.id}
                            activity={a}
                            canEdit={canEdit}
                            users={users}
                            activities={activities}
                            onStatusChange={handleStatusChange}
                            onQuickStatus={handleQuickStatus}
                            onAssign={handleAssign}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                        />
                    ))}
                </div>
            )}
            {canEdit && showCreate && (
                <CreateTaskDialog
                    unit={unit}
                    users={users}
                    activities={activities}
                    draft={draft.current}
                    onDraft={(d) => { draft.current = d; }}
                    onDiscard={() => { draft.current = null; }}
                    onClose={closeCreate}
                    onCreated={(assignee) => {
                        fetchActivities({ silent: true });
                        showToast({ text: assignee ? `Task created and assigned to ${assignee}` : 'Task created' });
                    }}
                />
            )}
        </div>
    );
}

ActivityTabV2.propTypes = { ctx: tabCtxShape.isRequired };
